import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Bell, Globe, MapPin, Home,
  Crosshair, Loader2, Megaphone, ShieldCheck, Ticket, Upload, LogIn, UserCog,
  Share2, ClipboardCheck, Plus, X, LifeBuoy,
} from 'lucide-react';
import clsx from 'clsx';
import AdBar from '../ads/AdBar';
import AuctionHousePanel from './AuctionHousePanel';
import SupportFooterLinks from './SupportFooterLinks';
import PastDueBanner from '../billing/PastDueBanner';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../services/api';
import { Link } from 'react-router-dom';

const AD_BAR_HEIGHT_PX = 50;
const FAB_GAP_ABOVE_AD_PX = 12;
const MOBILE_BOTTOM_INSET = `calc(${AD_BAR_HEIGHT_PX + FAB_GAP_ABOVE_AD_PX}px + env(safe-area-inset-bottom, 0px))`;

function ThisAreaButton() {
  const navigate = useNavigate();
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = useCallback(() => {
    if (!navigator.geolocation) {
      setError('GPS not available');
      return;
    }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await authFetch(
            `/api/neighbourhood/by-coords?lat=${latitude}&lng=${longitude}`
          );
          if (!res.ok) {
            setError('No data for this location');
            setLocating(false);
            return;
          }
          const data = await res.json();
          if (data.postcode) {
            navigate(`/neighbourhood/${encodeURIComponent(data.postcode)}`);
          } else {
            setError('Could not resolve postcode');
          }
        } catch {
          setError('Connection error');
        }
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        if (err.code === 1) setError('Location access denied');
        else if (err.code === 2) setError('Location unavailable');
        else setError('Location timed out');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, [navigate]);

  return (
    <div
      className="fixed right-4 z-50 sidebar-dock:hidden flex flex-col items-end gap-1"
      style={{ bottom: MOBILE_BOTTOM_INSET }}
    >
      {error && (
        <span className="text-xs text-red-400 bg-slate-900/90 border border-red-500/30 rounded-lg px-3 py-1.5">
          {error}
        </span>
      )}
      <button
        onClick={handleClick}
        disabled={locating}
        className={clsx(
          'flex items-center gap-2 px-5 py-3 rounded-full shadow-lg shadow-emerald-500/20 text-sm font-semibold transition-colors',
          locating
            ? 'bg-slate-700 text-slate-400 cursor-wait'
            : 'bg-emerald-500 text-white hover:bg-emerald-400 active:brightness-90'
        )}
      >
        {locating ? <Loader2 size={18} className="animate-spin" /> : <Crosshair size={18} />}
        This Area
      </button>
    </div>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1280px) and (orientation: landscape)');
    const onChange = () => {
      if (mq.matches) setSidebarOpen(false);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Platform admin = is_superuser. Role-based admin sections (auction-house
  // uploader, agent uploader, etc.) keep their own gating elsewhere; this flag
  // only controls the PropScope platform-admin sections at the bottom.
  const isAdmin = !!user?.is_superuser;

  // Tier-based: auction houses, estate agents, deal sourcers and white-label
  // users see the upload page. Superusers see it too so we can demo / spot-check.
  const canUpload =
    user?.is_superuser ||
    user?.subscription_tier === 'auction_house' ||
    user?.subscription_tier === 'estate_agent' ||
    user?.subscription_tier === 'deal_source' ||
    user?.subscription_tier === 'white_label';

  // Investor-only surfaces. Providers (auction houses, estate agents, deal
  // sourcers) don't manage portfolios, so the alerts / portfolio-share /
  // broker-CTA UX is hidden for them. Superusers see everything for support.
  const isInvestor =
    user?.is_superuser ||
    !user?.subscription_tier ||
    user?.subscription_tier === 'investor';

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', disabled: !user, disabledTitle: 'Sign in to view your dashboard' },
    { to: '/properties', icon: Building2, label: 'Properties' },
    { to: '/analysis', icon: ClipboardCheck, label: 'Desk Report', disabled: !user, disabledTitle: 'Sign in for property desk analysis' },
    { to: '/neighbourhood', icon: MapPin, label: 'Area Lookup' },
    { to: '/my-properties', icon: Home, label: 'My Properties', disabled: !user, disabledTitle: 'Sign in to manage your properties' },
    ...(user?.is_broker ? [{ to: '/broker/shared', icon: Share2, label: 'Shared with Me' }] : []),
    ...(canUpload ? [{ to: '/upload', icon: Upload, label: 'Upload Listings' }] : []),
    ...(isInvestor ? [{
      to: '/alerts',
      icon: Bell,
      label: 'Alerts',
      disabled: !user,
      disabledTitle: 'Sign in to set up property alerts',
    }] : []),
    ...(user ? [
      { to: '/account', icon: UserCog, label: 'Account' },
      { to: '/support', icon: LifeBuoy, label: 'Contact support' },
    ] : []),
    ...(isAdmin ? [
      { to: '/scrapers', icon: Globe, label: 'Data Sources' },
      { to: '/admin/ads', icon: Megaphone, label: 'Ads' },
      { to: '/admin/users', icon: ShieldCheck, label: 'Users' },
      { to: '/admin/promo-codes', icon: Ticket, label: 'Promo Codes' },
      { to: '/admin/login-log', icon: LogIn, label: 'Login Log' },
    ] : []),
  ];

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      {/* Slide-out nav — docked only on wide landscape (sidebar-dock breakpoint) */}
      <aside
        className={clsx(
          'fixed top-0 left-0 bottom-[50px] z-50 w-72 max-w-[85vw] bg-slate-900 border-r border-slate-800 transform transition-transform duration-300 ease-in-out flex flex-col',
          'sidebar-dock:relative sidebar-dock:translate-x-0 sidebar-dock:bottom-auto sidebar-dock:w-64 sidebar-dock:max-w-none sidebar-dock:h-[calc(100vh-50px)]',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full sidebar-dock:translate-x-0',
        )}
      >
        <div className="flex items-center justify-end px-3 pt-3 sidebar-dock:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800"
            aria-label="Close navigation menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Logo */}
        <NavLink
          to={user ? '/dashboard' : '/'}
          onClick={() => setSidebarOpen(false)}
          className="flex flex-col items-center gap-1 px-4 py-4 sidebar-dock:py-5 border-b border-slate-800 hover:bg-slate-800/30 transition-colors"
        >
          <img src="/propscope-logo.png" alt="PropScope"
            className="h-16 w-auto object-contain" />
          <p className="text-slate-500 text-[11px] tracking-wide text-center">
            Analyse Smarter. Invest Better.
          </p>
        </NavLink>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map(({ to, icon: Icon, label, disabled, disabledTitle }) =>
            disabled ? (
              <span
                key={to}
                aria-disabled="true"
                title={disabledTitle}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-600 cursor-not-allowed opacity-50 select-none"
              >
                <Icon size={18} />
                {label}
              </span>
            ) : (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                  )
                }
              >
                <Icon size={18} />
                {label}
              </NavLink>
            )
          )}

          <AuctionHousePanel onNavigate={() => setSidebarOpen(false)} />
        </nav>

        {/* Footer — sits above the 50px ad bar */}
        <div className="shrink-0 px-4 py-3 border-t border-slate-800 space-y-2">
          <p className="text-slate-500 text-[10px] leading-snug">
            © Geekybee Limited 2026. All Rights Reserved.
          </p>
          <p className="text-slate-600 text-[10px]">
            Data: Land Registry © Crown copyright
          </p>
          <p className="text-slate-600 text-[10px] space-x-2">
            <Link to="/terms" className="hover:text-emerald-400">Terms</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-emerald-400">Privacy</Link>
          </p>
          <SupportFooterLinks />
          <a
            href="mailto:info@geekybee.net?subject=PropScope%20sidebar%20advertising"
            className="block w-full text-center text-[10px] leading-snug text-slate-400 hover:text-emerald-400 border border-slate-700 hover:border-slate-600 rounded-lg px-2 py-2 transition-colors"
          >
            To advertise on this bar contact info@geekybee.net
          </a>
        </div>
      </aside>

      {/* Backdrop when slide-out nav is open (compact / portrait) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 sidebar-dock:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Bottom-left tab — opens nav shortcuts without stealing content width */}
      {!sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="fixed left-0 z-50 flex items-center gap-1.5 rounded-r-xl border border-l-0 border-slate-700/80 bg-slate-900/95 pl-2 pr-3 py-2.5 text-slate-300 shadow-lg backdrop-blur-sm transition-colors sidebar-dock:hidden hover:bg-slate-800 hover:text-white active:brightness-90"
          style={{ bottom: MOBILE_BOTTOM_INSET }}
          aria-label="Open navigation shortcuts"
        >
          <Plus size={18} className="text-emerald-400" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">
            Menu
          </span>
        </button>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden w-full">
        <PastDueBanner />
        {/* Compact header — logo only; nav opens via left tab */}
        <header className="flex items-center justify-center px-4 py-4 bg-slate-900 border-b border-slate-800 sidebar-dock:hidden">
          <NavLink to={user ? '/dashboard' : '/'}>
            <img src="/propscope-logo.png" alt="PropScope" className="h-12 w-auto max-h-14 object-contain" />
          </NavLink>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto pb-28 sidebar-dock:pb-[50px]">
          <Outlet />
        </main>
      </div>

      {/* Floating "This Area" GPS button — mobile/tablet only */}
      <ThisAreaButton />

      <AdBar />
    </div>
  );
}
